import React, { useState, useRef, useCallback, useEffect } from "react";
import { ArrowLeft, User, Camera, X, Image as ImageIcon } from "lucide-react";
import Cropper, { Area } from "react-easy-crop";
import getCroppedImg from "../../../../utils/cropImage";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useSelector } from "react-redux";
import styles from "./Step4CreatorProfile.module.css";
import parentStyles from "../CreatorApplication.module.css";
import MousePointerClick from "../icons/MousePointerClick";
import { showToast } from "../../../../utils/toast";

interface Step4Props {
  onNext: (data?: any) => void;
  onBack: () => void;
  savedData?: any;
  canGoBack?: boolean;
}

const schema = yup.object({
  firstName: yup.string().required("First name is required"),
  lastName: yup.string().required("Last name is required"),
  username: yup.string().required("Username is required"),
  headline: yup.string().required("Headline is required"),
  aboutMe: yup.string().required("About me is required"),
  lookingFor: yup.string().required("This field is required"),
}).required();

type FormData = yup.InferType<typeof schema>;

export default function Step4CreatorProfile({ onNext, onBack, savedData, canGoBack = true }: Step4Props) {
  const reduxUser = useSelector((state: any) => state.user?.user);
  let localUser: any = null;
  try {
    const stored = localStorage.getItem("user");
    if (stored) localUser = JSON.parse(stored);
  } catch (e) { }

  const initialUsername =
    savedData?.username ||
    reduxUser?.username ||
    localUser?.username ||
    "";

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [profileImageFile, setProfileImageFile] = useState<File | null>(savedData?.profileImg || null);
  const [profileImage, setProfileImage] = useState<string | null>(savedData?.profileImagePreview || null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const onCropComplete = useCallback((croppedArea: Area, currentCroppedAreaPixels: Area) => {
    setCroppedAreaPixels(currentCroppedAreaPixels);
  }, []);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      const reader = new FileReader();
      reader.addEventListener("load", () => {
        setImageSrc(reader.result?.toString() || null);
      });
      reader.readAsDataURL(file);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleSaveCrop = async () => {
    try {
      if (imageSrc && croppedAreaPixels) {
        const croppedImageFile = await getCroppedImg(imageSrc, croppedAreaPixels);
        if (croppedImageFile) {
          const previewUrl = URL.createObjectURL(croppedImageFile);
          setProfileImage(previewUrl);
          setProfileImageFile(croppedImageFile);
          setIsModalOpen(false);
          setImageSrc(null);
        }
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleCancel = () => {
    setImageSrc(null);
    setIsModalOpen(false);
  };

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      firstName: savedData?.firstName || reduxUser?.firstName || localUser?.firstName || "",
      lastName: savedData?.lastName || reduxUser?.lastName || localUser?.lastName || "",
      username: initialUsername,
      headline: savedData?.headline || "",
      aboutMe: savedData?.aboutMe || "",
      lookingFor: savedData?.lookingFor || savedData?.whatAreYouLookingFor || "",
    },
  });

  useEffect(() => {
    if (initialUsername) {
      setValue("username", initialUsername, { shouldValidate: true });
    }
  }, [initialUsername, setValue]);

  const onSubmit = (data: FormData) => {
    const payload = {
      profileImg: profileImageFile,
      profileImagePreview: profileImage,
      firstName: data.firstName.trim(),
      lastName: data.lastName.trim(),
      username: data.username.trim(),
      headline: data.headline?.trim(),
      aboutMe: data.aboutMe.trim(),
      lookingFor: data.lookingFor.trim(),
      whatAreYouLookingFor: data.lookingFor.trim(),
    };
    onNext(payload);
  };

  return (
    <div className={parentStyles.formCard}>
      <h1 className={styles.formTitle}>Build your creator profile</h1>
      <p className={styles.formSubtitle}>
        This is what your audience will see. You can update it at any time from your dashboard.
      </p>

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <div className={styles.photoUploadContainer}>
          <div className={styles.photoCircle} onClick={() => setIsModalOpen(true)} style={profileImage ? { overflow: 'hidden', padding: 0 } : {}}>
            {profileImage ? (
              <img src={profileImage} alt="Profile preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <User size={32} className={styles.photoIcon} strokeWidth={1} />
            )}
            <div className={styles.cameraBadge}>
              <Camera size={12} color="#ffffff" />
            </div>
          </div>
          <div className={styles.photoTitle}>
            Upload a profile photo<span>*</span>
          </div>
          <div className={styles.photoInstruction}>
            JPEG or PNG, min 400x400px. Used on your public profile.
          </div>
        </div>

        <div className={styles.formGrid}>
          <div className={styles.inputGroup}>
            <label className={styles.label}>First name <span>*</span></label>
            <input
              type="text"
              className={styles.input}
              placeholder="Enter first name"
              {...register("firstName")}
            />
            {errors.firstName && <p className={parentStyles.errorText}>{errors.firstName.message}</p>}
          </div>
          <div className={styles.inputGroup}>
            <label className={styles.label}>Last name <span>*</span></label>
            <input
              type="text"
              className={styles.input}
              placeholder="Enter last name"
              {...register("lastName")}
            />
            {errors.lastName && <p className={parentStyles.errorText}>{errors.lastName.message}</p>}
          </div>
        </div>

        <div className={styles.formGrid}>
          <div className={styles.inputGroup}>
            <label className={styles.label}>Username <span>*</span></label>
            <input
              type="text"
              className={styles.input}
              placeholder="Choose a unique username"
              {...register("username")}
            />
            {errors.username && <p className={parentStyles.errorText}>{errors.username.message}</p>}
          </div>
          <div className={styles.inputGroup}>
            <label className={styles.label}>Headline <span>*</span></label>
            <input
              type="text"
              className={styles.input}
              placeholder="Short headline that describes you"
              {...register("headline")}
            />
            {errors.headline && <p className={parentStyles.errorText}>{errors.headline.message}</p>}
          </div>
        </div>

        <div className={styles.inputGroupFull}>
          <label className={styles.label}>About Me <span>*</span></label>
          <textarea
            className={styles.textarea}
            placeholder="Tell in short"
            {...register("aboutMe")}
          />
          {errors.aboutMe && <p className={parentStyles.errorText}>{errors.aboutMe.message}</p>}
        </div>

        <div className={styles.inputGroupFull}>
          <label className={styles.label}>What you're looking for <span>*</span></label>
          <textarea
            className={styles.textarea}
            placeholder=""
            {...register("lookingFor")}
          />
          {errors.lookingFor && <p className={parentStyles.errorText}>{errors.lookingFor.message}</p>}
        </div>

        <div className={styles.actionRow}>
          <button
            type="button"
            className={styles.backBtn}
            onClick={() => {
              if (!canGoBack) {
                showToast("Back not allowed", "info");
                return;
              }
              onBack();
            }}
            title={!canGoBack ? "Back not allowed" : undefined}
            style={!canGoBack ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
          >
            <ArrowLeft size={18} /> Back
          </button>
          <button type="submit" className={styles.continueBtn}>
            Continue
          </button>
        </div>
      </form>

      {isModalOpen && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <button className={styles.closeBtn} onClick={() => setIsModalOpen(false)}>
              <X size={20} />
            </button>
            <h2 className={styles.modalTitle}>Upload profile picture</h2>

            {imageSrc ? (
              <>
                <div className={styles.cropperContainer}>
                  <Cropper
                    image={imageSrc}
                    crop={crop}
                    zoom={zoom}
                    aspect={1}
                    cropShape="round"
                    showGrid={false}
                    onCropChange={setCrop}
                    onCropComplete={onCropComplete}
                    onZoomChange={setZoom}
                  />
                </div>
                <div className={styles.controlsContainer}>
                  <ImageIcon className={styles.zoomIcon} />
                  <input
                    type="range"
                    value={zoom}
                    min={1}
                    max={3}
                    step={0.1}
                    aria-labelledby="Zoom"
                    onChange={(e) => setZoom(Number(e.target.value))}
                    className={styles.slider}
                  />
                  <ImageIcon className={styles.zoomIcon} />
                </div>
                <div className={styles.modalActionRow}>
                  <button type="button" className={styles.modalCancelBtn} onClick={handleCancel}>Cancel</button>
                  <button type="button" className={styles.modalUploadBtn} onClick={handleSaveCrop}>Upload</button>
                </div>
              </>
            ) : (
              <>
                <div className={styles.uploadArea} onClick={handleUploadClick}>
                  <div className={styles.uploadIconContainer}>
                    <MousePointerClick />
                  </div>
                  <div className={styles.uploadText}>
                    Drag and drop JPEG,<br />PNG, SVG or GIF (max.<br />800x400 px)
                  </div>
                </div>

                <div className={styles.orDivider}>or</div>

                <button type="button" className={styles.uploadOutlineBtn} onClick={handleUploadClick}>
                  Upload A Photo
                </button>

                <div className={styles.modalActionRow}>
                  <button type="button" className={styles.modalCancelBtn} onClick={handleCancel}>
                    Cancel
                  </button>
                  <button type="button" className={styles.modalUploadBtn} disabled style={{ opacity: 0.5, cursor: 'not-allowed' }}>
                    Upload
                  </button>
                </div>
              </>
            )}

            <input
              type="file"
              accept="image/jpeg, image/png, image/svg+xml, image/gif"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
          </div>
        </div>
      )}
    </div>
  );
}
